// profileSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the profile slice
const initialState = {
  profileData: null,
  status: "idle",
  updateProfileSuccess: false,
  updateEmailSuccess: false, // Added new state for email update success
};

// Destructure Http function
const { httpRequest } = Http();

// Async thunk to fetch user profile
export const fetchUserProfile = createAsyncThunk(
  "fetchUserProfile",
  async ({ navigate, setLoader }, { rejectWithValue }) => {
    try {
      const response = await httpRequest(
        {
          method: "GET",
          url: "me",
        },
        navigate
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred");
    }
  }
);

// updateUserProfile thunk
export const updateUserProfile = createAsyncThunk(
  "updateUserProfile",
  async ({ profileData, navigate, setLoader }, { rejectWithValue }) => {
    try {
      const response = await httpRequest(
        {
          method: "PUT",
          url: "admin/profile",
          body: profileData,
        },
        navigate
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred");
    }
  }
);

// updateEmail thunk (just sends the verification email)
export const updateEmail = createAsyncThunk(
  "updateEmail",
  async ({ emailData, navigate, setLoader }, { rejectWithValue }) => {
    try {
      const response = await httpRequest(
        {
          method: "PUT", // or PUT depending on your API
          url: "admin/profile/email/verification",
          body: emailData,
        },
        navigate
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred");
    }
  }
);

// Profile slice
const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    // Reducer to reset profile state
    resetProfileState: (state) => {
      state.updateProfileSuccess = false;
      state.updateEmailSuccess = false; // Reset email success state
      state.status = "idle";
    },
    // Reducer to manually set profile data
    setProfileData: (state, action) => {
      state.profileData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle pending actions
      .addCase(fetchUserProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateEmail.pending, (state) => {
        state.status = "loading";
        state.updateEmailSuccess = false;
      })

      // Handle fulfilled actions
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.profileData = action.payload.user;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.updateProfileSuccess = true;
        state.profileData = action.payload;
      })
      .addCase(updateEmail.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.updateEmailSuccess = true;
        state.profileData = action.payload?.user || state.profileData; // Update profile if needed
        // toastHandler(true, messages.profile.emailUpdateSuccess);
      })

      // Handle rejected actions
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.status = "failed";
        toastHandler(false, action.payload || messages.profile.fetchError);
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.status = "failed";
        state.updateProfileSuccess = false;
        toastHandler(false, action.payload || messages.profile.updateError);
      })
      .addCase(updateEmail.rejected, (state, action) => {
        state.status = "failed";
        state.updateEmailSuccess = false;
        toastHandler(
          false,
          action.payload || messages.profile.emailUpdateError
        );
      });
  },
});

// Export actions
export const { resetProfileState, setProfileData } = profileSlice.actions;

// Export reducer
export default profileSlice.reducer;
